import React, { useEffect, useMemo, useState } from "react";
import { listCategories, listProducts } from "@/lib/api";
import { Search, X, Package, ScanBarcode } from "lucide-react";

/**
 * Modal de búsqueda de artículo estilo Sage (rejilla densa + filtros).
 * Devuelve el producto seleccionado (y la variante si aplica) vía onPick.
 */
export default function ProductFinder({ onClose, onPick }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null); // for variant picker

  useEffect(() => {
    Promise.all([listCategories(), listProducts({ limit: 500 })]).then(([c, p]) => {
      setCategories(c);
      setProducts(p);
    });
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (active !== "all") list = list.filter((p) => p.category_id === active);
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(qq) ||
          p.sku.toLowerCase().includes(qq) ||
          (p.barcode || "").includes(qq) ||
          (p.variants || []).some((v) => (v.barcode || "").includes(qq))
      );
    }
    return list;
  }, [products, active, q]);

  // Detectar código de barras (Enter tras numérico)
  const handleKey = (e) => {
    if (e.key === "Enter" && q.length >= 8) {
      // exact match
      for (const p of products) {
        if (p.barcode === q) return onPick({ product: p });
        if (p.variants) {
          const v = p.variants.find((v) => v.barcode === q);
          if (v) return onPick({ product: p, variant: v });
        }
      }
    }
  };

  if (selectedProduct?.has_variants) {
    return (
      <VariantPicker
        product={selectedProduct}
        onBack={() => setSelectedProduct(null)}
        onClose={onClose}
        onPick={(variant) => onPick({ product: selectedProduct, variant })}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl h-[80vh] rounded-md border border-slate-300 flex flex-col shadow-2xl">
        {/* title bar Sage */}
        <div className="h-8 bg-[#0F172A] text-white px-3 flex items-center justify-between text-sm">
          <span>BÚSQUEDA DE ARTÍCULOS</span>
          <button onClick={onClose} className="h-6 w-6 hover:bg-white/20 rounded flex items-center justify-center">
            <X size={14} />
          </button>
        </div>

        {/* filters */}
        <div className="bg-[#F5F5F5] border-b border-slate-300 p-2 flex items-center gap-2">
          <ScanBarcode size={16} className="text-slate-500 ml-1" />
          <div className="relative flex-1">
            <Search size={14} className="absolute top-2 left-2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Buscar artículo, SKU o código de barras…"
              className="w-full h-8 pl-8 pr-2 bg-white border border-slate-300 text-sm focus:outline-none focus:border-slate-600"
            />
          </div>
          <select
            value={active}
            onChange={(e) => setActive(e.target.value)}
            className="h-8 px-2 bg-white border border-slate-300 text-sm"
          >
            <option value="all">Todas las familias</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* table */}
        <div className="flex-1 overflow-y-auto thin-scroll">
          <table className="w-full text-sm">
            <thead className="bg-[#E6E6E6] sticky top-0 text-[12px] text-slate-700">
              <tr className="border-b border-slate-300">
                <th className="text-left px-3 py-1.5 font-medium">SKU</th>
                <th className="text-left px-3 py-1.5 font-medium">Artículo</th>
                <th className="text-right px-3 py-1.5 font-medium">PVP</th>
                <th className="text-right px-3 py-1.5 font-medium">IVA</th>
                <th className="text-right px-3 py-1.5 font-medium">Stock</th>
                <th className="text-center px-3 py-1.5 font-medium">Variantes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => p.has_variants ? setSelectedProduct(p) : onPick({ product: p })}
                  className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer"
                >
                  <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">{p.sku}</td>
                  <td className="px-3 py-1.5">{p.name}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{p.price.toFixed(2)}€</td>
                  <td className="px-3 py-1.5 text-right text-slate-500">{(p.tax_rate * 100).toFixed(0)}%</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${p.stock <= 10 ? "text-red-600" : ""}`}>{p.stock}</td>
                  <td className="px-3 py-1.5 text-center text-[11px]">
                    {p.has_variants ? <span className="px-1.5 bg-pink-100 text-pink-700 rounded">{p.variants?.length || 0}</span> : "—"}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm"><Package size={32} className="mx-auto mb-2 opacity-50" />Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="h-8 bg-[#F5F5F5] border-t border-slate-300 px-3 flex items-center justify-between text-xs text-slate-600">
          <span>{filtered.length} registros</span>
          <span className="font-mono">Enter para seleccionar · Esc cierra</span>
        </div>
      </div>
    </div>
  );
}

function VariantPicker({ product, onBack, onClose, onPick }) {
  const tallas = [...new Set(product.variants.map((v) => v.talla).filter(Boolean))];
  const colors = [...new Set(product.variants.map((v) => v.color).filter(Boolean))];
  const [talla, setTalla] = useState(tallas[0]);
  const [color, setColor] = useState(colors[0]);
  const variant = product.variants.find((v) => v.talla === talla && v.color === color);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-md border border-slate-300 shadow-2xl">
        <div className="h-8 bg-[#0F172A] text-white px-3 flex items-center justify-between text-sm">
          <span>SELECCIÓN DE VARIANTE · {product.name}</span>
          <button onClick={onClose} className="h-6 w-6 hover:bg-white/20 rounded flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs text-slate-500 mb-1.5">Talla</div>
            <div className="flex flex-wrap gap-1.5">
              {tallas.map((t) => (
                <button
                  key={t}
                  onClick={() => setTalla(t)}
                  className={`h-9 min-w-[44px] px-3 border text-sm font-medium ${
                    talla === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-500"
                  }`}
                >{t}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1.5">Color</div>
            <div className="flex flex-wrap gap-1.5">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`h-9 px-3 border text-sm font-medium ${
                    color === c ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-500"
                  }`}
                >{c}</button>
              ))}
            </div>
          </div>
          {variant && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">SKU variante</span><span className="font-mono">{product.sku}-{variant.sku_suffix}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">PVP</span><span className="font-mono font-semibold">{(variant.price_override ?? product.price).toFixed(2)}€</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Stock</span><span className={`font-mono ${variant.stock <= 5 ? "text-red-600" : ""}`}>{variant.stock} ud</span></div>
            </div>
          )}
        </div>
        <div className="p-3 bg-[#F5F5F5] border-t border-slate-300 flex justify-between">
          <button onClick={onBack} className="h-9 px-4 text-sm border border-slate-300 bg-white hover:bg-slate-100">Volver</button>
          <button
            disabled={!variant}
            onClick={() => onPick(variant)}
            className="h-9 px-4 text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-300"
          >Añadir a venta</button>
        </div>
      </div>
    </div>
  );
}
